import React, { useState } from 'react';
import { gql } from '@apollo/client';
import {
  BooleanInput,
  NumberInput,
  SelectInput,
  Datagrid,
  FunctionField,
  useUpdate,
  useRefresh,
  useNotify,
  useRecordContext,
} from 'react-admin';
import { Box, Chip } from '@mui/material';
import { format } from 'date-fns';
import List from 'components/List';
import MarketInput from 'components/MarketInput';
import PositionInput from 'components/PositionInput';
import DateFilter from 'components/Filter/DateFilter';
import LinkField from 'components/LinkField';
import MoneyField from 'components/MoneyField';
import ButtonWithConfirmation from 'components/ButtonWithConfirmation';
import ApprovalConfirmDialog from './ApprovalConfirmDialog';
import { titleize } from 'inflection';
import { RejectGeneratedRateModal } from './RejectGeneratedRateModal';

const statusChoices = [
  { id: 'pending_review', name: 'Pending Review' },
  { id: 'approved', name: 'Approved' },
  { id: 'auto_approved', name: 'Auto Approved' },
  { id: 'rejected', name: 'Rejected' },
  { id: 'expired', name: 'Expired' },
];

const GeneratedRatesListFilters = [
  <MarketInput key="market" alwaysOn resettable source="search.scope_id" />,
  <PositionInput key="position" alwaysOn emptyText="All" source="search.position_id" />,
  <BooleanInput
    key="show_only_latest_rate"
    label="Show Only Latest Rate"
    source="search.show_only_latest_rate"
    alwaysOn
  />,
  <SelectInput
    key="status"
    label="Status"
    source="search.status"
    choices={statusChoices}
    emptyText="All"
    alwaysOn
  />,
  <NumberInput
    key="min_variance"
    label="Min. Variance"
    source="search.minimum_variance_percentage"
  />,
  <DateFilter key="last_updated" label="Last Updated" source="search.updated_at_lte" alwaysOn />,
  <DateFilter key="deadline" label="Deadline until" source="search.review_deadline" alwaysOn />,
];

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return '';
  }
  try {
    return format(new Date(dateString), 'MM/dd/yyyy');
  } catch {
    return '';
  }
};

const formatTimeToFill = (hours: number | null | undefined): string => {
  if (!hours && hours !== 0) {
    return '';
  }
  if (hours < 24) {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours} hours ${minutes ? `and ${minutes} minutes` : ''}`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = Math.floor(hours % 24);
  const minutes = Math.round((hours % 1) * 60);
  return `${days} days, ${remainingHours} hours ${minutes ? `and ${minutes} minutes` : ''}`;
};

const ActionButtons = () => {
  const record = useRecordContext();
  const [update, { isLoading }] = useUpdate();
  const refresh = useRefresh();
  const notify = useNotify();
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  if (!record) {
    return null;
  }

  const handleReject = async () => {
    try {
      await update('AutoGeneratedRate', { id: record.id, data: { _action: 'REJECT' } });
      notify('Rate rejected', { type: 'success' });
      refresh();
    } catch {
      notify('Error: Rate not rejected', { type: 'error' });
    }
  };

  const handleApprove = async () => {
    try {
      await update('AutoGeneratedRate', { id: record.id, data: { _action: 'APPROVE' } });
      notify('Rate approved', { type: 'success' });
      refresh();
    } catch {
      notify('Error: Rate not approved', { type: 'error' });
    }
  };

  const isPendingReview = record.status === 'pending_review';

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <ButtonWithConfirmation
          size="small"
          variant="contained"
          color="success"
          onClick={handleApprove}
          loading={isLoading}
          disabled={!isPendingReview}
          confirmationTitle="Approve Rate"
          confirmationContent="Are you sure you want to approve this generated rate?"
        >
          Approve
        </ButtonWithConfirmation>
        <ButtonWithConfirmation
          size="small"
          variant="contained"
          color="error"
          onClick={handleReject}
          loading={isLoading}
          disabled={!isPendingReview}
          confirmationTitle="Reject Rate"
          confirmationContent="Are you sure you want to reject this generated rate?"
        >
          Reject
        </ButtonWithConfirmation>
      </Box>
      <ApprovalConfirmDialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        record={record}
      />
      <RejectGeneratedRateModal
        record={record}
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
      />
    </>
  );
};

const GeneratedRatesList = () => {
  const statusColor = (status: string) => {
    switch (status) {
      case 'pending_review':
        return 'warning';
      case 'approved':
        return 'success';
      case 'auto_approved':
        return 'info';
      case 'rejected':
        return 'error';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <List
      filters={GeneratedRatesListFilters}
      exporter={false}
      perPageOptions={[25, 50, 100, 150, 250]}
      filterDefaultValues={{ search: { show_only_latest_rate: true } }}
    >
      <Datagrid bulkActionButtons={false}>
        <LinkField label="Market" source="market.display_name" sortBy="scope_name" />
        <LinkField label="Position" source="position.display_name" sortBy="position_name" />
        <LinkField label="Specialty" source="specialty.label" sortBy="specialty_name" />
        <MoneyField label="New Rate" source="generated_rate_cents" blankIfNull />
        <MoneyField label="Current Rate" source="current_rate_cents" blankIfNull />
        <FunctionField
          label="Variance"
          render={(record) => (record?.variance !== null ? `${record.variance.toFixed(2)}%` : '')}
          sortBy="variance"
        />
        <FunctionField
          label="Date Generated"
          render={(record) => formatDate(record?.created_at)}
          sortBy="created_at"
        />
        <FunctionField
          label="Deadline"
          render={(record) => formatDate(record?.review_deadline)}
          sortBy="review_deadline"
        />
        <FunctionField
          label="Status"
          sortBy="status"
          render={(record) => {
            return (
              <Box sx={{ minWidth: 110 }}>
                <Chip
                  color={statusColor(record?.status)}
                  label={titleize(record?.status)}
                  size="small"
                />
              </Box>
            );
          }}
        />
        <FunctionField label="Actions" render={() => <ActionButtons />} />

        <FunctionField
          label="Last Approved"
          render={(record) => formatDate(record?.last_approved?.approved_at)}
          sortBy="last_approved_at"
        />
        <FunctionField
          label="Last Approved Type"
          render={(record) => record?.last_approved?.type || ''}
          sortBy="last_approved_type"
        />
        <FunctionField
          label="GSV (L3M)"
          render={(record) => {
            const gsv = record?.metrics_data?.gsv;
            if (gsv === null || gsv === undefined) {
              return '';
            }
            return <MoneyField source="gsv" record={{ gsv }} blankIfNull />;
          }}
          sortBy="metrics_data_gsv"
        />
        <FunctionField
          label="Fill Rate (L3M)"
          render={(record) => {
            const fillRate = record?.metrics_data?.fill_rate;
            return fillRate !== null && fillRate !== undefined ? `${fillRate}%` : '';
          }}
          sortBy="metrics_data_fill_rate"
        />
        <FunctionField
          label="Supply Level Category"
          render={(record) => record?.metrics_data?.supply_level_category || ''}
          sortBy="metrics_data_supply_level_category"
        />
        <FunctionField
          label="Supply Utilization Category"
          render={(record) => record?.metrics_data?.supply_utilization_category || ''}
          sortBy="metrics_data_supply_utilization_category"
        />
        <FunctionField
          label="Time to Fill"
          render={(record) => (
            <Box sx={{ minWidth: 110 }}>
              {formatTimeToFill(record?.metrics_data?.hours_to_fill_last_3_months)}
            </Box>
          )}
          sortBy="metrics_data_hours_to_fill_last_3_months"
        />
      </Datagrid>
    </List>
  );
};

GeneratedRatesList.query = gql`
  query autoGeneratedRates($input: AutoGeneratedRateQueryInput) {
    _autoGeneratedRatesMeta(input: $input) {
      count
    }
    autoGeneratedRates(input: $input) {
      id
      status
      review_deadline
      created_at
      generated_rate_cents
      current_rate_cents
      variance
      scope_type
      scope_id
      market {
        id
        display_name
      }
      position_id
      position {
        id
        display_name
      }
      specialty_id
      specialty {
        id
        label
      }
      last_approved {
        approved_at
        type
      }
      metrics_data {
        gsv
        fill_rate
        supply_level_category
        supply_utilization_category
        hours_to_fill_last_3_months
      }
    }
  }
`;

export default GeneratedRatesList;
